import { Mediator, payload } from "modular-openscriptjs";
import { approved, eParse, inform } from "../helpers";
import { api, STRINGS } from "../declarations";
import { $e } from "../ojs.events";
import { channelRoutes } from "../echo-channels";

export default class CompanyMediator extends Mediator {
    $$company = {
        needs: {
            update: (ed, event) => this.updateCompany(ed, event),
        },
        staff: {
            needs: {
                creation: (ed, event) => this.sendInvitation(ed, event),
                deletion: (ed, event) => this.deleteStaff(ed, event),
            },
            invitation: {
                needs: {
                    cancellation: (ed, event) =>
                        this.cancelInvitation(ed, event),
                },
            },
        },
        has: {
            changed: (ed, event) => this.resetNotificationChannels(ed),
        },
    };

    async resetNotificationChannels(ed) {
        const { message } = eParse(ed);

        const ws = app("EchoEventListener");

        let previous = message.previous;
        let current = message.current;

        if(previous) {
          ws.stopListeningToNotifications(channelRoutes.company(previous.id));
        }

        if(current) {
          ws.listenToNotifications(channelRoutes.company(current.id))
        }
    }

    async updateCompany(ed, event) {
        const { message } = eParse(ed);

        const res = await api.put(`companies/${message.id}`, message);

        if (!res) {
            inform("Failed to update business information", STRINGS.ERROR);
            return this.send($e.company.has.updateError, ed);
        }

        inform("Business information updated successfully", STRINGS.SUCCESS);
        this.send($e.company.updated, payload({ company: res.data.company }));
    }

    async sendInvitation(ed, event) {
        const { message } = eParse(ed);

        const res = await api.post(
            `companies/${message.companyId}/invitations`,
            {
                identifier: message.identifier,
                role: message.role,
            },
        );

        if (!res) {
            inform("Failed to send invitation", STRINGS.ERROR);
            return this.send($e.company.staff.invitation.has.creationError, ed);
        }

        inform("Invitation sent successfully!", STRINGS.SUCCESS);
        this.send(
            $e.company.staff.invitation.created,
            payload({ invitation: res.data.invitation }),
        );
    }

    async cancelInvitation(ed, event) {
        if (
            !approved(
                event,
                ed,
                "Are you sure you want to cancel this invitation?",
            )
        )
            return;

        const { message, meta } = eParse(ed);

        let res = await api.delete(
            `companies/${message.companyId}/invitations/${message.id}`,
        );

        if (!res) {
            inform("Failed to cancel invitation", STRINGS.ERROR);
            return this.send(
                $e.company.staff.invitation.has.cancellationError,
                ed,
                meta,
            );
        }

        inform("Invitation cancelled", STRINGS.SUCCESS);

        this.send(
            $e.company.staff.invitation.cancelled,
            payload({ invitation: res.data.invitation }),
        );
    }

    async deleteStaff(ed, event) {
        if (
            !approved(
                event,
                ed,
                "Are you sure you want to remove this staff member?",
            )
        )
            return;

        const { message } = eParse(ed);

        const res = await api.delete(
            `companies/${message.companyId}/staff/${message.id}`,
        );

        if (!res) {
            inform("Failed to remove staff member", STRINGS.ERROR);
            return this.send($e.company.staff.has.deletionError, ed);
        }

        inform("Staff member removed successfully", STRINGS.SUCCESS);
        this.send($e.company.staff.deleted, payload({ id: message.id }));
    }
}
